import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { ClerkLoaded, ClerkLoading, ClerkProvider } from "@clerk/react";
import * as Sentry from "@sentry/react";
import { Button, clerkAppearanceFor } from "@/design-system";
import { useTheme } from "@/lib/theme";
import { initSentry } from "@/lib/sentry";
import { initPosthog } from "@/lib/posthog";
import { queryClient } from "./lib/query-client";
import { InstallAuthToken } from "./lib/install-auth-token";
import { ResolveOrgTask } from "./lib/resolve-org-task";
import { SentryIdentity } from "./lib/sentry-identity";
import { PosthogIdentity } from "./lib/posthog-identity";
import { routeTree } from "./route-tree.gen";
import "./styles/globals.css";

// Before React mounts, so init errors and render crashes are captured too.
initSentry();
initPosthog();

// Router context is typed so every loader/route file gets the query client
// without prop-drilling. See https://tanstack.com/router/latest/docs/framework/react/guide/router-context
const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0, // let React Query own staleness
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
if (!publishableKey) {
  throw new Error("VITE_CLERK_PUBLISHABLE_KEY is required");
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("missing #root in index.html");

function BootSplash() {
  return (
    <div className="min-h-screen bg-bg-1 flex items-center justify-center text-fg-2">
      Loading…
    </div>
  );
}

function CrashFallback() {
  return (
    <div className="min-h-screen bg-bg-1 flex flex-col gap-4 items-center justify-center text-fg-2">
      <p>Something went wrong.</p>
      <Button onClick={() => window.location.reload()}>Reload</Button>
    </div>
  );
}

function App() {
  // Theme drives Clerk's baseTheme; the .light class on <html> is set by
  // the inline boot script in index.html and reasserted by setTheme().
  const theme = useTheme();
  return (
    <ClerkProvider
      publishableKey={publishableKey}
      afterSignOutUrl="/sign-in"
      appearance={clerkAppearanceFor(theme)}
    >
      <ClerkLoading>
        <BootSplash />
      </ClerkLoading>
      <ClerkLoaded>
        <InstallAuthToken />
        <ResolveOrgTask />
        <SentryIdentity />
        <PosthogIdentity />
        <QueryClientProvider client={queryClient}>
          <Sentry.ErrorBoundary fallback={<CrashFallback />}>
            <RouterProvider router={router} />
          </Sentry.ErrorBoundary>
        </QueryClientProvider>
      </ClerkLoaded>
    </ClerkProvider>
  );
}

function dismissSplash() {
  const splash = document.getElementById("splash");
  if (!splash) return;
  splash.classList.add("splash-out");
  splash.addEventListener(
    "transitionend",
    () => splash.remove(),
    { once: true },
  );
}

createRoot(rootEl).render(
  <StrictMode>
    <App />
  </StrictMode>,
);

dismissSplash();
